import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { Grid, LinearProgress, Card, CardContent, CardMedia, Typography } from '@material-ui/core';
import { withStyles, createMuiTheme, responsiveFontSizes } from '@material-ui/core/styles';
import get from 'lodash/get';

import { shouldUseApplePay, shouldUseGooglePay } from '../../services/functions/PaymentFunctions';
import * as Config from '../../../config.json';
import styles from '../../css/paymentMethodPage/PaymentMethodPage.scss';
import { getCurrentUserPaymentOptions, getCurrentOrder, getCompany, getLocations } from '../../selectors';
import PaymentMethodComponent from './subComponents/PaymentMethodComponent';
import Button from '../core/components/Button';
import * as FeatureFlags from '../../../feature-flags.json';
import Routes from '../../services/routes/Routes.json';
import ResponsiveStyles from '../../../jsonStyles/components/paymentMethodPage/PaymentMethodPage.style.json';
import MasterIcon from '../../assets/images/master.svg';
import VisaIcon from '../../assets/images/visa.svg';
import AmexIcon from '../../assets/images/american-express-1.png';
import DeleteDialog from '..//paymentPage/components/DeleteDialog';

let theme = createMuiTheme();
const responsiveStyles = typeof styles === 'function' ? styles() : styles;
theme = responsiveFontSizes(theme);

responsiveStyles.buttonContainer = {
  ...responsiveStyles.buttonContainer,
  [theme.breakpoints.down('sm')]: {
    width: ResponsiveStyles.buttonContainer.width,
  },
};

responsiveStyles.noPaymentOptionsCard = {
  ...responsiveStyles.noPaymentOptionsCard,
  [theme.breakpoints.down('sm')]: {
    width: ResponsiveStyles.noPaymentOptionsCard.width,
  },
};

responsiveStyles.cardMedia = {
  ...responsiveStyles.cardMedia,
  [theme.breakpoints.down('sm')]: {
    width: ResponsiveStyles.cardMedia.width,
  },
};

responsiveStyles.gridItem = {
  ...responsiveStyles.gridItem,
  [theme.breakpoints.down('sm')]: {
    minHeight: ResponsiveStyles.gridItem.minHeight,
  },
};

class PaymentMethodPage extends Component {
  constructor(props) {
    super(props);
    this.state = {
      paymentOptionToDelete: null,
      deleteDialogOpen: false,
    };
  }
  async componentDidMount() {
    const {
      actions, user, history, company,
    } = this.props;

    if (user && user.guestToken) {
      history.push(Routes.path.menuPage);
    }
    if (user) {
      try {
        await actions.getAllResources(user.token, ['users', user.id, 'payment_options']);
      } catch (error) {
        console.log('API call error', error);
      }
    } else {
      history.push(Routes.path.menuPage);
    }

    let googlePayCompany;
    if (!company) {
      const response = await actions.getAllResources(null, ['companies']);
      googlePayCompany = response.response;
    } else {
      googlePayCompany = company;
    }
    if (shouldUseGooglePay(googlePayCompany, false)) {
      try {
        await this.initializeSquare();
      } catch (e) {
        console.error('Initializing Card failed', e);
      }
    }
  }

  onClickDelete = (payOpt) => {
    this.setState({ paymentOptionToDelete: payOpt, deleteDialogOpen: true });
  };

  initializeDigitalPay = async (squarePayments) => {
    const { company, currentOrder } = this.props;
    const countryCode = get(company, 'countryCode') || 'US';
    const currencyCode = get(currentOrder, 'location.currencyCode') || 'USD';
    const orderTotal = 0;
    const paymentRequest = squarePayments.paymentRequest({
      countryCode,
      currencyCode,
      total: {
        amount: orderTotal.toString(),
        label: 'Total',
      },
    });

    const digitalPay = await squarePayments.googlePay(paymentRequest);
    await digitalPay.attach('#google-pay-button-payment-method', { buttonSizeMode: 'fill', buttonType: 'long' });
  }

  initializeSquare = async () => {
    const { currentOrder, locations } = this.props;
    const appId = Config.general.squareAppId;
    const locationId = currentOrder.location ? get(currentOrder, 'location.squareId') : locations[0].squareId;
    const payments = window.Square.payments(appId, locationId);
    await this.initializeDigitalPay(payments);
  }

  addNewCard = () => {
    const { history } = this.props;
    history.push(Routes.path.addNewCardPage);
  }

  shouldRenderPaymentOptions = () => {
    const { company, paymentOptions } = this.props;
    return shouldUseApplePay(company, false) || shouldUseGooglePay(company, false) || paymentOptions.length > 0;
  }

  deletePaymentOption = () => {
    const { user, actions } = this.props;
    const { paymentOptionToDelete } = this.state;
    if (user) {
      actions.deleteResource(user, ['users', user.id, 'payment_options', paymentOptionToDelete.id]);
      this.setState({ paymentOptionToDelete: null, deleteDialogOpen: false });
    }
  };

  renderApplePay = () => {
    const { classes } = this.props;
    const appleButtonStyle = {
      display: 'inline-block',
      WebkitAppearance: '-apple-pay-button',
      ApplePayButtonType: 'plain',
      ApplePayButtonStyle: 'black',
      width: '100%',
      margin: '0 0 0 0',
      height: '100%',
    };
    return (
      <Grid
        item
        xs={parseInt(responsiveStyles.grid.xsItem, 10)}
        sm={parseInt(responsiveStyles.grid.smItem, 10)}
        className={classes.gridItem}
      >
        <button
          style={appleButtonStyle}
          id="apple-pay-button"
        />
      </Grid>
    );
  }

  renderGooglePay = () => {
    const { classes } = this.props;
    return (
      <Grid
        item
        xs={parseInt(responsiveStyles.grid.xsItem, 10)}
        sm={parseInt(responsiveStyles.grid.smItem, 10)}
        className={classes.gridItem}
      >
        <div
          id="google-pay-button-payment-method"
          role="button"
          tabIndex={0}
          aria-label="google pay button"
          className={classes.googlePayContainer}
        />
      </Grid>
    );
  }

  renderNoPaymentOptions = () => {
    const { classes, onPressAddCard, translation } = this.props;
    return (
      <div className={classes.noPaymentOptionsCardContainer}>
        <Card className={classes.noPaymentOptionsCard}>
          <CardContent className={classes.noPaymentOptionsCardContent}>
            <Typography className={classes.noPaymentOptionsText}>{translation('PaymentMethodPage.noPaymentOptions')}</Typography>
            <div className={classes.cardMediaContainer}>
              <CardMedia
                className={classes.cardMedia}
                component="img"
                image={VisaIcon}
              />
              <CardMedia
                className={classes.cardMedia}
                component="img"
                image={MasterIcon}
              />
              <CardMedia
                className={classes.cardMedia}
                component="img"
                image={AmexIcon}
              />
            </div>
            <div className={classes.cardButton}>
              <Button
                type="primary"
                text={translation('PaymentMethodPage.addNewCard')}
                onClick={onPressAddCard}
                styleOverride={responsiveStyles.addNewCardButton}
              />
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  render() {
    const {
      paymentOptions, loading, translation, classes, user, actions, company, onPressAddCard,
    } = this.props;
    const { deleteDialogOpen, paymentOptionToDelete } = this.state;
    return FeatureFlags.PaymentMethodPage.show && (
      <div>
        {
          loading !== 0
            ? <LinearProgress />
            : (
              <div className={classes.paymentMethodPage}>
                <div className={classes.paymentMethodsContainer}>
                  {
                    this.shouldRenderPaymentOptions()
                      ? (
                        <div>
                          <Grid container spacing={responsiveStyles.grid.spacing}>
                            {
                              shouldUseApplePay(company, false)
                              && this.renderApplePay()
                            }
                            {
                              shouldUseGooglePay(company, false)
                              && this.renderGooglePay()
                            }
                            {
                              paymentOptions && paymentOptions.map(paymentOption =>
                              (
                                <Grid
                                  item
                                  xs={parseInt(responsiveStyles.grid.xsItem, 10)}
                                  sm={parseInt(responsiveStyles.grid.smItem, 10)}
                                  className={classes.gridItem}
                                  key={paymentOption.id}
                                >
                                  <PaymentMethodComponent
                                    paymentOption={paymentOption}
                                    user={user}
                                    translation={translation}
                                    actions={actions}
                                    onClickDelete={this.onClickDelete}
                                  />
                                </Grid>
                              ))
                            }
                          </Grid>
                          <div className={classes.buttonContainer}>
                            <Button
                              type="primary"
                              text={translation('PaymentMethodPage.addNewCard')}
                              onClick={onPressAddCard}
                              styleOverride={responsiveStyles.addNewCardButton}
                            />
                          </div>
                        </div>
                      ) : this.renderNoPaymentOptions()
                  }
                </div>
                {
                  deleteDialogOpen
                  && (
                    <DeleteDialog
                      open={deleteDialogOpen}
                      translation={translation}
                      cardType={paymentOptionToDelete && paymentOptionToDelete.cardType}
                      cardName={paymentOptionToDelete && paymentOptionToDelete.cardNumber}
                      handleClick={this.deletePaymentOption}
                      handleCloseDeleteDialog={() => this.setState({ deleteDialogOpen: false })}
                    />
                  )
                }
              </div>
            )
        }
      </div>
    );
  }
}

PaymentMethodPage.propTypes = {
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  actions: PropTypes.objectOf(PropTypes.func).isRequired,
  loading: PropTypes.number.isRequired,
  translation: PropTypes.func.isRequired,
  paymentOptions: PropTypes.arrayOf(PropTypes.object),
  user: PropTypes.objectOf(PropTypes.any),
  history: PropTypes.objectOf(PropTypes.any).isRequired,
  company: PropTypes.objectOf(PropTypes.any).isRequired,
  onPressAddCard: PropTypes.func.isRequired,
};

PaymentMethodPage.defaultProps = {
  user: null,
  paymentOptions: [],
};

const mapStateToProps = state => ({
  paymentOptions: getCurrentUserPaymentOptions(state),
  currentOrder: getCurrentOrder(state),
  company: getCompany(state),
  locations: getLocations(state),
});

export default connect(mapStateToProps, null)(withStyles(responsiveStyles)(withRouter(PaymentMethodPage)));
